/**
 * @class BoundedVisibility
 * @extends HTMLElement
 *
 * A custom element adding class when it is scrolled into view between two boundary elements.
 */
class BoundedVisibility extends HTMLElement {
  constructor() {
    super();
  }

  get _modifier() {
    return this.getAttribute("visible-class-name") || "is-visible";
  }

  get _fromElement() {
    return document.querySelector(this.getAttribute("from"));
  }

  get _toElement() {
    return document.querySelector(this.getAttribute("to"));
  }

  connectedCallback() {
    if (!this._fromElement || !this._toElement) {
      return;
    }

    this._observer = new IntersectionObserver(this._onIntersection.bind(this), {
      root: null,
      threshold: 0,
    });

    this._observer.observe(this._fromElement);
    this._observer.observe(this._toElement);
  }

  disconnectedCallback() {
    if (this._observer) {
      this._observer.disconnect();
    }
  }

  _onIntersection(entries) {
    entries.forEach((entry) => {
      if (entry.target === this._fromElement) {
        this._isFromElementScrolledPast = entry.boundingClientRect.bottom < 0;
      }

      if (entry.target === this._toElement) {
        this._isToElementScrolledPast = entry.boundingClientRect.bottom < 0;
      }
    });

    if (this._isFromElementScrolledPast && !this._isToElementScrolledPast) {
      this.classList.add(this._modifier);
    } else {
      this.classList.remove(this._modifier);
    }
  }
}

if (!customElements.get("bounded-visibility")) {
  customElements.define("bounded-visibility", BoundedVisibility);
}

/**
 * @class ConditionalVisibility
 * @extends HTMLElement
 *
 * A custom element adding class when an observed element is not in view.
 */
class ConditionalVisibility extends BoundedVisibility {
  constructor() {
    super();
  }

  get _observeElement() {
    return document.querySelector(this.getAttribute("observe"));
  }

  connectedCallback() {
    if (!this._observeElement) {
      return;
    }

    this._observer = new IntersectionObserver(this._onIntersection.bind(this), {
      root: null,
      threshold: 0,
    });

    this._observer.observe(this._observeElement);

    if (this._fromElement) {
      this._observer.observe(this._fromElement);
    }

    if (this._toElement) {
      this._observer.observe(this._toElement);
    }
  }

  _onIntersection(entries) {
    entries.forEach((entry) => {
      if (this._fromElement) {
        if (entry.target === this._fromElement) {
          this._isFromElementScrolledPast = entry.boundingClientRect.bottom < 0;
        }
      }

      if (entry.target === this._observeElement) {
        this._isObservableElementVisible = entry.isIntersecting;
      }

      if (this._toElement) {
        if (entry.target === this._toElement) {
          this._isToElementVisible = entry.isIntersecting;
        }
      }
    });

    const fromCondition = this._fromElement ? this._isFromElementScrolledPast : true;
    const toCondition = this._toElement ? !this._isToElementVisible : true;

    if (fromCondition && !this._isObservableElementVisible && toCondition) {
      this.classList.add(this._modifier);
    } else {
      this.classList.remove(this._modifier);
    }
  }
}

if (!customElements.get("conditional-visibility")) {
  customElements.define("conditional-visibility", ConditionalVisibility);
}
